import { type FC, useCallback, useEffect, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Portal } from '@mui/material'
import type { UUID } from '@/index'
import { useAuthStatus } from '@/features/user'
import type { BlockingBucket } from './types'
import { DEFAULT_DIALOG_WIDTH_PX } from '@/features/events/conversation/dialogs'
import { BLOCKING_BUCKET_ORDER, BLOCKING_INBOX_Z_INDEX } from './constants'
import { useBlockingBuckets, useInboxQuickAllow } from './hooks'
import { useSessionDialogManager } from './context'
import { BlockingBucketCounter, BlockingInboxPanel } from './components'
import type { BlockingInboxEntry } from './types'

interface OpenPanelState {
  readonly bucket: BlockingBucket
  readonly anchorEl: HTMLElement
}

export const BlockingInbox: FC = () => {
  const { isAuthenticated } = useAuthStatus()
  const { entriesByBucket, counts, hasAny } = useBlockingBuckets()
  const { openSessionEvent } = useSessionDialogManager()
  const { allowOnce } = useInboxQuickAllow()
  const [openPanel, setOpenPanel] = useState<OpenPanelState | null>(null)
  // Sessions with an in-flight "allow once" submit. Keyed by sessionId so the
  // card can disable its trigger and we never fire a duplicate before the
  // optimistic clear (or a failure) resolves.
  const [submittingSessionIds, setSubmittingSessionIds] = useState<ReadonlySet<UUID>>(
    () => new Set(),
  )
  // Tracks the counter that opened the panel so dismissing (Escape / outside
  // click) can restore focus to it. NOT used on select — jumping to a session
  // moves focus into its dialog instead.
  const lastAnchorRef = useRef<HTMLElement | null>(null)

  const handleOpen = useCallback((bucket: BlockingBucket, anchorEl: HTMLElement): void => {
    lastAnchorRef.current = anchorEl
    setOpenPanel({ bucket, anchorEl })
  }, [])

  const handleClose = useCallback((viaKeyboard = false): void => {
    setOpenPanel(null)
    // Restore focus to the triggering counter only on a KEYBOARD dismiss
    // (Escape). On a mouse close, the programmatic refocus would trigger
    // :focus-visible and leave an unwanted blue outline on the counter.
    if (viaKeyboard) lastAnchorRef.current?.focus()
  }, [])

  const handleSelect = useCallback(
    (sessionId: UUID): void => {
      setOpenPanel(null)
      openSessionEvent(sessionId)
    },
    [openSessionEvent],
  )

  const handleAllowOnce = useCallback(
    async (entry: BlockingInboxEntry): Promise<void> => {
      const { sessionId } = entry.session
      setSubmittingSessionIds((prev) => {
        if (prev.has(sessionId)) return prev
        const next = new Set(prev)
        next.add(sessionId)
        return next
      })

      try {
        await allowOnce(entry)
      } catch {
        // Submission failed: the card stays (no recordCommand). The error is
        // surfaced via the hook's onError; nothing else to do at the HUD level.
      } finally {
        setSubmittingSessionIds((prev) => {
          if (!prev.has(sessionId)) return prev
          const next = new Set(prev)
          next.delete(sessionId)
          return next
        })
      }
    },
    [allowOnce],
  )

  // Clear the panel state once its bucket drains (allow-once, or resolution via
  // SSE / terminal). Otherwise openPanel stays non-null: the other counters stay
  // dimmed, and a later event for that bucket reopens the panel anchored to the
  // now-removed counter element — which MUI Popper renders at the far-left. No
  // focus restore here (the anchor counter may have unmounted).
  useEffect(() => {
    if (openPanel && entriesByBucket[openPanel.bucket].length === 0) {
      setOpenPanel(null)
    }
  }, [openPanel, entriesByBucket])

  if (!isAuthenticated || !hasAny) return null

  return (
    <Portal>
      <Box
        className="mui-fixed"
        sx={inboxContainerSx}
        role="region"
        aria-label="Blocking events inbox"
      >
        {BLOCKING_BUCKET_ORDER.map((bucket) => (
          <BlockingBucketCounter
            key={bucket}
            bucket={bucket}
            count={counts[bucket]}
            isActive={openPanel?.bucket === bucket}
            isDimmed={openPanel !== null && openPanel.bucket !== bucket}
            onOpen={handleOpen}
          />
        ))}

        {openPanel && entriesByBucket[openPanel.bucket].length > 0 && (
          <BlockingInboxPanel
            bucket={openPanel.bucket}
            anchorEl={openPanel.anchorEl}
            open={true}
            entries={entriesByBucket[openPanel.bucket]}
            submittingSessionIds={submittingSessionIds}
            onClose={handleClose}
            onSelect={handleSelect}
            onAllowOnce={handleAllowOnce}
          />
        )}
      </Box>
    </Portal>
  )
}

const inboxContainerSx: SxProps<Theme> = {
  position: 'fixed',
  top: 22,
  // Track the centered session dialog's right border: on wide screens the
  // dialog's right inset is (100vw - dialogWidth)/2; the 16px floor matches the
  // dialog's own `m: 2` margin once the viewport narrows below the dialog width.
  right: `max(24px, calc((100vw - ${DEFAULT_DIALOG_WIDTH_PX}px) / 2 + 8px))`,
  zIndex: BLOCKING_INBOX_Z_INDEX,
  display: { xs: 'none', sm: 'flex' },
  alignItems: 'center',
  gap: 2.5,
}
